import { useState } from 'react'
import { useAuthStore } from '../store/authStore'
import { useGameStore } from '../store/gameStore'
import Button from './Button'
import { Mail, Lock, User, Save } from 'lucide-react'
import { useI18n } from '../i18n/I18nProvider'

function InlineAuthForm({ initialMode = 'signup', shouldSave = true, onSuccess }) {
  const { t } = useI18n()
  const [mode, setMode] = useState(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const signIn = useAuthStore((state) => state.signIn)
  const signUp = useAuthStore((state) => state.signUp)
  const seeds = useGameStore((state) => state.seeds)
  const currentPlant = useGameStore((state) => state.currentPlant)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setMessage('')
    setLoading(true)

    try {
      if (mode === 'signin') {
        const { error } = await signIn(email, password)
        if (error) throw error
        if (onSuccess) onSuccess('signin')
      } else if (mode === 'signup') {
        if (!username) {
          throw new Error(t('inlineAuth.errors.usernameRequired'))
        }
        const { error } = await signUp(email, password, username)
        if (error) throw error
        setMessage(t('inlineAuth.messages.accountCreated'))
        if (onSuccess) onSuccess('signup')
      }
    } catch (err) {
      setError(err.message || t('inlineAuth.errors.generic'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-center text-green-800 mb-4">
        {mode === 'signin' && t('auth.signIn')}
        {mode === 'signup' && (shouldSave ? t('auth.saveProgress') : t('auth.signUp'))}
      </h2>

      {shouldSave && mode === 'signup' && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Save size={20} className="text-green-600" />
            <span className="font-bold text-green-800">{t('inlineAuth.progress.title')}</span>
          </div>
          <ul className="text-sm text-gray-700 space-y-1">
            {currentPlant && <li>✓ {t('inlineAuth.progress.growingPlant')}</li>}
            {seeds > 0 && (
              <li>
                ✓ {seeds}{' '}
                {t(`inlineAuth.progress.seed${seeds > 1 ? 'Plural' : 'Singular'}`)}
              </li>
            )}
          </ul>
          <p className="text-xs text-gray-600 mt-2">
            {t('inlineAuth.progress.footer')}
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-3 text-sm">
          {error}
        </div>
      )}

      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded mb-3 text-sm">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        {mode === 'signup' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.usernameLabel')}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                placeholder={t('auth.usernamePlaceholder')}
                required
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('auth.emailLabel')}
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              placeholder={t('auth.emailPlaceholder')}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('auth.passwordLabel')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              placeholder={t('auth.passwordPlaceholder')}
              required
              minLength={6}
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          loading={loading}
        >
          {mode === 'signin' && t('auth.signInButton')}
          {mode === 'signup' && t('auth.signUpButton')}
        </Button>
      </form>

      <div className="mt-4 text-center text-xs text-gray-600">
        {mode === 'signin' && (
          <>
            <span>{t('auth.noAccount')} </span>
            <button
              onClick={() => setMode('signup')}
              className="text-green-600 hover:text-green-700 font-medium"
            >
              {t('auth.signUpButton')}
            </button>
          </>
        )}

        {mode === 'signup' && (
          <>
            <span>{t('auth.hasAccount')} </span>
            <button
              onClick={() => setMode('signin')}
              className="text-green-600 hover:text-green-700 font-medium"
            >
              {t('auth.signInButton')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default InlineAuthForm
